/**
 * Client-side recovery helpers.
 *
 * Currently exports `clearSiteStateAndReload`, the implementation
 * behind the "Reset session & reload" affordance on
 * `(lobby)/error.tsx` and `global-error.tsx`.
 *
 * The motivating bug: production-only Next 14.2.x router-cache crash
 * documented in `(lobby)/error.tsx` — `fillLazyItemsTillLeafWithHead`
 * throws `Cannot read properties of null (reading 'get')`, the lobby
 * boundary mounts, the original `<Link>` in the fallback prefetched
 * `/`, the prefetch re-ran the same now-corrupt `useMemo`, the
 * fallback re-mounted, and React's update-depth limiter eventually
 * tripped #185. The first user who hit this got unstuck by deleting
 * cookies from DevTools — specifically a stale Privy auth cookie that
 * was auto-resuming the session on the same tick the framework's
 * `initialParallelRoutes` got freed. This helper exposes the same
 * remedy as a single click, with no DevTools required:
 *
 *   1. POST `/api/recovery/reset-session` — the server expires every
 *      cookie the request carries (including HttpOnly Privy auth
 *      cookies that JS on the page can't touch) via `Set-Cookie:
 *      Max-Age=0`. Best-effort: a network failure is logged and we
 *      still proceed to step 2.
 *   2. Wipe all client-visible storage:
 *        - `localStorage` (Wagmi `wagmi.*`, WalletConnect `wc@2:*`,
 *          our chat draft + tutorial + onboarding state, …)
 *        - `sessionStorage` (preview-mode banner state, lobby HUD)
 *        - non-HttpOnly cookies left over from edge cases the server
 *          step missed (tracking pixels, analytics)
 *        - IndexedDB databases (Privy stores its full session here on
 *          modern browsers, plus `keyval-store` from idb-keyval used
 *          by some wallet SDKs).
 *   3. Hard-navigate to `/` — full document load. Clears every
 *      in-memory map including Next's router cache, all React state,
 *      every provider's store, every event listener.
 *
 * The whole sequence is wrapped in try/catch at the leaf — we MUST
 * end with a `location.replace('/')` even if step 1 or 2 partially
 * fails, because the alternative is leaving the user staring at the
 * same broken error card.
 *
 * This is deliberately destructive (the user is in a degraded state
 * and explicitly opted to reset). UI copy on the trigger surfaces
 * that they'll be logged out and lose drafts — see `(lobby)/error.tsx`
 * and `global-error.tsx` for the wording.
 */

const RECOVERY_ENDPOINT = '/api/recovery/reset-session';

/**
 * Best-effort delete of every cookie the document can see (i.e.,
 * non-HttpOnly). HttpOnly cookies are cleared by the server endpoint
 * before this runs.
 *
 * For each cookie we set the same name to an empty value with a past
 * expiry on `path=/` and on the bare path of the current location;
 * browsers won't expire a cookie unless the (name, path, domain)
 * triple of the deletion matches the original `Set-Cookie`, and the
 * two attempts here cover the two paths Next/Privy/our own routes
 * have used in practice. Domain is left unset — the browser scopes
 * to the current host, which is what we want.
 */
function clearVisibleCookies(): void {
  if (typeof document === 'undefined') return;
  const cookies = document.cookie.split(';');
  for (const raw of cookies) {
    const eq = raw.indexOf('=');
    const name = (eq > -1 ? raw.slice(0, eq) : raw).trim();
    if (!name) continue;
    // Two paths: the broad `/` scope used by Privy/Next, and the
    // current pathname (catches anything domain-scoped to a deeper
    // segment that escaped the broad delete).
    const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = `${name}=; ${expiry}; path=/`;
    if (window.location.pathname !== '/') {
      document.cookie = `${name}=; ${expiry}; path=${window.location.pathname}`;
    }
  }
}

/**
 * Best-effort wipe of every IndexedDB database visible to the page.
 * Privy stores its full session graph in IndexedDB on modern browsers,
 * plus `keyval-store` (from `idb-keyval`, used transitively by a
 * couple of wallet SDKs). `indexedDB.databases()` is widely supported
 * (Chromium 71+, Safari 14+, Firefox 126+); when the API is missing
 * we silently no-op rather than try to enumerate by name (we don't
 * keep that list up to date and a stale list isn't worth maintaining).
 *
 * Returns a promise that resolves when every delete completes or after
 * a short timeout — IndexedDB can hang indefinitely if a connection
 * from another tab is open, and the recovery flow MUST complete.
 */
async function clearIndexedDb(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  // `indexedDB.databases` is a relatively new API; bail quietly when
  // it's missing rather than throwing.
  const databases = (indexedDB as IDBFactory & {
    databases?: () => Promise<IDBDatabaseInfo[]>;
  }).databases;
  if (typeof databases !== 'function') return;
  let infos: IDBDatabaseInfo[] = [];
  try {
    infos = await databases.call(indexedDB);
  } catch {
    return;
  }
  await Promise.race([
    Promise.allSettled(
      infos.map(
        (info) =>
          new Promise<void>((resolve) => {
            if (!info.name) return resolve();
            const req = indexedDB.deleteDatabase(info.name);
            const done = () => resolve();
            req.onsuccess = done;
            req.onerror = done;
            req.onblocked = done;
          }),
      ),
    ),
    // Hard 1.5s ceiling so a single hung database doesn't block the
    // whole recovery. Anything still alive after the timeout will be
    // wiped on the next successful page load anyway (the auth cookie
    // is already gone by then).
    new Promise<void>((resolve) => window.setTimeout(resolve, 1500)),
  ]);
}

/**
 * Reset every piece of per-user state we control, then hard-reload to
 * the homepage. Safe to call from any client surface — boundary
 * fallbacks, settings menus, recovery dialogs.
 *
 * Sequence:
 *   1. POST `/api/recovery/reset-session` so the server expires
 *      HttpOnly auth cookies that JS can't touch.
 *   2. Wipe `localStorage`, `sessionStorage`, visible cookies,
 *      IndexedDB databases — in that order (cheapest synchronous
 *      stuff first so the slow IndexedDB step doesn't block the
 *      fast wins on a browser that hangs there).
 *   3. `location.replace('/')` — full-document navigation, drops
 *      every in-memory map. We use `replace` instead of `assign` so
 *      the broken page doesn't end up in history; `Back` from the
 *      fresh `/` should land on whatever the user had open before
 *      the crash, not on the error card.
 *
 * Every step is wrapped in try/catch and we always end at the
 * `location.replace`; the recovery is a last-resort affordance and
 * we'd rather leave the user with the cookies still present (they
 * can manually clear) than with the error card stuck on screen.
 */
export async function clearSiteStateAndReload(): Promise<void> {
  if (typeof window === 'undefined') return;

  // Step 1: server-side cookie expiry. `keepalive: true` so the
  // request survives even if the page navigates before it lands —
  // the boundary is in a degraded state and the user might tab away,
  // and we still want the cookies cleared.
  try {
    await fetch(RECOVERY_ENDPOINT, {
      method: 'POST',
      keepalive: true,
      // The endpoint reads no body, but we send a tiny one so
      // `sendBeacon`-style edge handling treats this as a real POST.
      body: '{}',
      headers: { 'Content-Type': 'application/json' },
      // Don't follow redirects — the endpoint returns plain JSON.
      redirect: 'manual',
    });
  } catch (error) {
    console.warn('[recovery] reset-session endpoint failed', error);
    // Continue regardless — we still want to clear what we can client-side.
  }

  // Step 2a: synchronous wipes (cheapest first).
  try {
    window.localStorage.clear();
  } catch (error) {
    console.warn('[recovery] localStorage.clear failed', error);
  }
  try {
    window.sessionStorage.clear();
  } catch (error) {
    console.warn('[recovery] sessionStorage.clear failed', error);
  }
  try {
    clearVisibleCookies();
  } catch (error) {
    console.warn('[recovery] cookie clear failed', error);
  }

  // Step 2b: IndexedDB (slow path; bounded by internal timeout).
  try {
    await clearIndexedDb();
  } catch (error) {
    console.warn('[recovery] indexedDB clear failed', error);
  }

  // Step 3: full-document load. `replace` so the broken card doesn't
  // end up in browser history.
  try {
    window.location.replace('/');
  } catch {
    // Last-resort: assign to href if replace blew up for any reason.
    window.location.href = '/';
  }
}
